import * as React from 'react';
import { IntlProvider } from 'react-intl';

import { useExtensionDebugger } from 'src/hooks/useExtensionDebugger';
import { usePostMessageHandler } from './PostMessageProvider';

interface LocaleProps {
  children: React.ReactNode;
}

interface LocaleContext {
  currency: string;
  locale: string;
  setCurrency: (currency: string) => void;
  setLocale: (locale: string) => void;
}

const DEFAULT_CURRENCY = 'USD';
const DEFAULT_LOCALE = 'en-US';

const LocaleContext = React.createContext<LocaleContext>({
  currency: DEFAULT_CURRENCY,
  locale: DEFAULT_LOCALE,
  setCurrency: () => {
    throw new Error('LocaleProvider not configured');
  },
  setLocale: () => {
    throw new Error('LocaleProvider not configured');
  },
});

export function LocaleProvider(props: LocaleProps) {
  const { children } = props;

  const [currency, setCurrency] = React.useState(DEFAULT_CURRENCY);
  const [overrideCurrency, setOverrideCurrency] = React.useState<string | null>(null);
  const [locale, setLocale] = React.useState(navigator.language);
  const { debugLog } = useExtensionDebugger();

  // Log locale/currency to the debug extension
  React.useEffect(() => {
    debugLog(`Locale set in CTA: currency=${currency}, locale=${locale}`);
  }, [debugLog, currency, locale]);

  // If new locale info is provided in the init event, try that instead
  usePostMessageHandler('init', (e) => {
    if ('locale' in e.detail.config) {
      const { locale } = e.detail.config;
      if (locale.language) {
        setLocale(locale.language);
      }
      if (locale.currencyCode) {
        setOverrideCurrency(locale.currencyCode);
      }
    }
  });

  const value = {
    currency: overrideCurrency || currency,
    locale,
    setCurrency,
    setLocale,
  };

  return (
    <LocaleContext.Provider value={value}>
      <IntlProvider locale={locale}>
        {children}
      </IntlProvider>
    </LocaleContext.Provider>
  );
}

export const useLocale = () => React.useContext(LocaleContext);
